<template lang="pug" src="./ai.pug"></template>

<i18n>
{
    "de": {
        "up to": "bis zu",
        "AI disclosure display": "Anzeige der KI-Kennzeichnung",
        "Always visible": "Immer sichtbar",
        "Icon visible, details on hover": "Symbol sichtbar, Details bei Mouseover",
        "No visible marker": "Keine sichtbare Kennzeichnung",
        "Applies to media marked as AI-generated. Can be overridden per image.": "Gilt für Medien, die als KI-generiert gekennzeichnet sind. Pro Bild überschreibbar.",
        "Without a visible marker you are responsible for meeting the disclosure duty another way.": "Ohne sichtbare Kennzeichnung sind Sie dafür verantwortlich, die Offenlegungspflicht anderweitig zu erfüllen.",
        "Marker colours": "Farben der Kennzeichnung",
        "Background": "Hintergrund",
        "Text colour": "Textfarbe",
        "AI marker symbol": "KI",
        "AI marker generated": "Generiert",
        "Leave empty to keep the storefront default (dark, translucent, white type).": "Leer lassen für die Standarddarstellung des Shops (dunkel, transparent, weiße Schrift).",
        "Generative media": "Generative Medien",
        "Controls which AI model, quality and resolution are used to generate a product image or video. Anyone starting a generation can override these for that one run; otherwise these defaults apply.": "Legt fest, welches KI-Modell, welche Qualität und welche Auflösung beim Erstellen eines Produktbilds oder -videos verwendet werden. Wer eine Generierung startet, kann dies für diesen einen Lauf überschreiben, ansonsten gelten diese Standardwerte.",
        "Image": "Bild",
        "Video": "Video",
        "Default video model": "Standard-Videomodell",
        "Default image-edit model": "Standard-Bildbearbeitungsmodell",
        "Applies to {models}. ": "Gilt für {models}. ",
        "Default: {value}": "Standard: {value}",
        "Default image quality": "Standard-Bildqualität",
        "quality.low": "Niedrig",
        "quality.medium": "Mittel",
        "quality.high": "Hoch",
        "Higher quality costs more per generation. Used whenever a run does not pick a quality itself.": "Höhere Qualität kostet mehr pro Generierung. Wird verwendet, wenn beim Erstellen keine Qualität ausgewählt wird.",
        "Default maximum edge length": "Standard-Maximalkantenlänge",
        "Shrinks the source before it is sent to the AI, which lowers cost, but permanently limits every future version of that image to this size on its longer edge.": "Verkleinert die Quelle vor dem Senden an die KI, was die Kosten senkt, aber jede zukünftige Version dieses Bildes dauerhaft auf diese Größe an der längeren Kante begrenzt.",
        "Default video resolution": "Standard-Videoauflösung",
        "Higher resolution costs more per generation. Used whenever a run does not pick a resolution itself.": "Höhere Auflösung kostet mehr pro Generierung. Wird verwendet, wenn beim Erstellen keine Auflösung ausgewählt wird.",
        "unit.seconds": "Sekunde | Sekunden",
        "unit.images": "Bild | Bilder",
        "Image descriptions": "Bildbeschreibungen",
        "Language models": "Sprachmodelle",
        "Which AI model writes the chatbot answers and the generated texts. A larger model writes better and costs more per token.": "Welches KI-Modell die Antworten des Chatbots und die erstellten Texte schreibt. Ein größeres Modell schreibt besser und kostet mehr pro Token.",
        "Which AI model writes the chatbot answers. A larger model writes better and costs more per token.": "Welches KI-Modell die Antworten des Chatbots schreibt. Ein größeres Modell schreibt besser und kostet mehr pro Token.",
        "Which AI model writes the generated texts. A larger model writes better and costs more per token.": "Welches KI-Modell die erstellten Texte schreibt. Ein größeres Modell schreibt besser und kostet mehr pro Token.",
        "Chatbot model": "Modell des Chatbots",
        "Fast and economical": "Schnell und günstig",
        "Best writing, slower": "Beste Texte, langsamer",
        "KI-Inhalte model": "Modell der KI-Inhalte",
        "{input} input, {output} output per 1M tokens": "{input} Input, {output} Output pro 1 Mio. Tokens",
        "AI bulk processing": "KI-Stapelverarbeitung",
        "Lets the media library apply one prompt template to many images at once. Every image is generated and billed individually, so a large selection takes a while and costs accordingly.": "Ermöglicht es, in der Mediathek eine Prompt-Vorlage auf viele Bilder gleichzeitig anzuwenden. Jedes Bild wird einzeln generiert und abgerechnet, eine große Auswahl dauert also entsprechend lange und verursacht entsprechende Kosten."
    },
    "en": {
        "unit.seconds": "second | seconds",
        "unit.images": "image | images",
        "AI marker symbol": "AI",
        "AI marker generated": "Generated",
        "quality.low": "Low",
        "quality.medium": "Medium",
        "quality.high": "High",
        "Applies to {models}. ": "Applies to {models}. ",
        "Default: {value}": "Default: {value}",
        "{input} input, {output} output per 1M tokens": "{input} input, {output} output per 1M tokens"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useAuthStore, useSettingsStore, useSetupStore } from '@/stores';
import api from '@/extensions/generative-media/store/api/generativeMedia';
import SettingField from './Partials/SettingField';
import ModelSelectOption from '@/extensions/generative-media/components/ModelSelectOption';
import textGenerationApi from '@/store/api/textGeneration';
import { moneyFormat, preciseMoneyFormat } from '@/filters';

export default {
    name: 'AiSettings',
    components: {
        SettingField,
        ModelSelectOption,
    },
    data() {
        return {
            loading: false,
            videoModels: [],
            imageEditModels: [],
            // per feature key: the language models the tenant can choose (EX_AI_TextModels)
            textModels: {},
        };
    },
    computed: {
        // The shared CMS form — saved as one payload by the parent Form.vue footer.
        form() {
            return useSettingsStore().form;
        },
        settings() {
            return _.get(this.form, 'generativeMediaSettings');
        },
        contentSettings() {
            return _.get(this.form, 'aiContentSettings');
        },
        // The AI text features of this CMS whose model the tenant chooses, in the order of the pickers.
        textModelFeatures() {
            const activeExtensions = (_.get(this.form, 'extensions') || []).map(extension => extension.key);

            return [
                { key: 'chatbot', setting: 'chatbot_model_key', label: this.$t('Chatbot model') },
                { key: 'ai-content', setting: 'ai_content_model_key', label: this.$t('KI-Inhalte model') },
            ].filter(feature => activeExtensions.includes(feature.key));
        },
        // Names only the features whose picker is shown.
        textModelIntro() {
            const keys = this.textModelFeatures.map(feature => feature.key);
            if (keys.length === 1) {
                return keys[0] === 'chatbot'
                    ? this.$t('Which AI model writes the chatbot answers. A larger model writes better and costs more per token.')
                    : this.$t('Which AI model writes the generated texts. A larger model writes better and costs more per token.');
            }
            return this.$t('Which AI model writes the chatbot answers and the generated texts. A larger model writes better and costs more per token.');
        },
        cmsId() {
            const id = _.get(useSetupStore().activeCms, 'id') || _.get(this.form, 'id');
            return id ? parseInt(id, 10) : null;
        },
        // The model pickers are only meaningful with a generation feature licensed;
        // the disclosure setting above applies regardless.
        hasGenerativeMedia() {
            const extensions = _.get(this.form, 'extensions') || [];
            return !!extensions.find(extension => ['product-video', 'ai-image-edit'].includes(extension.key));
        },
        // The template's own `ai` settings block (includes/settings/ai.yml), read the
        // same way the APIs tab reads its `api` block. Templates that predate the
        // block simply yield no fields and the section hides itself.
        templateSettings() {
            const settings = _.get(this.form, 'config.template.settings') || [];
            return settings.find(el => el.key === 'ai');
        },
        aiSettingFields() {
            const groups = _.get(this.templateSettings, 'groups') || [];
            return groups.flatMap(group => group.fields || []);
        },
        settingValues() {
            return useSettingsStore().values;
        },
        enabledExtensions() {
            const supportedExtensions = _.get(this.form, 'config.template.extensions') || [];
            const activeExtensions = (_.get(this.form, 'extensions') || []).map(extension => extension.key);
            return ['status-logs', ...supportedExtensions].filter(extension => activeExtensions.includes(extension));
        },
        labelDisplayModes() {
            return [
                { value: 'permanent', label: this.$t('Always visible') },
                { value: 'hover', label: this.$t('Icon visible, details on hover') },
                { value: 'none', label: this.$t('No visible marker') },
            ];
        },
        labelDisplayHint() {
            if (this.aiLabelDisplay === 'none') {
                return this.$t('Without a visible marker you are responsible for meeting the disclosure duty another way.');
            }
            return this.$t('Applies to media marked as AI-generated. Can be overridden per image.');
        },
        aiLabelDisplay: {
            get() {
                return _.get(this.form, 'generativeMediaSettings.ai_label_display') || 'permanent';
            },
            set(value) {
                // A shop that never used generative media has no settings row yet, so
                // create the object rather than dropping the value on the floor.
                const form = useSettingsStore().form;
                if (!form.generativeMediaSettings) {
                    this.$set(form, 'generativeMediaSettings', {});
                }
                this.$set(form.generativeMediaSettings, 'ai_label_display', value);
            },
        },
        // Presets are the storefront's own default plus the obvious light counterpart,
        // so the common cases are one click and the picker still allows anything.
        labelBackgroundPresets() {
            return ['rgba(0, 0, 0, 0.8)', 'rgba(255, 255, 255, 0.85)', '#000000', '#ffffff'];
        },
        labelTextPresets() {
            return ['#ffffff', '#000000'];
        },
        labelPreviewStyle() {
            return {
                background: this.aiLabelBackgroundColor || 'rgba(0, 0, 0, 0.8)',
                color: this.aiLabelTextColor || '#ffffff',
            };
        },
        aiLabelBackgroundColor: {
            get() {
                return _.get(this.form, 'generativeMediaSettings.ai_label_background_color') || null;
            },
            set(value) {
                this.writeGenerativeMediaSetting('ai_label_background_color', value || null);
            },
        },
        aiLabelTextColor: {
            get() {
                return _.get(this.form, 'generativeMediaSettings.ai_label_text_color') || null;
            },
            set(value) {
                this.writeGenerativeMediaSetting('ai_label_text_color', value || null);
            },
        },
        // Super users only, and deliberately not gated on the mimic flag for the action
        // itself: mimicking swaps the JWT, so the API sees the tenant admin. Here we are
        // only deciding whether to render the control, which is safe either way.
        canSeeBulkSetting() {
            const authStore = useAuthStore();
            return authStore.isSuperUser || authStore.isImpersonatedBySuperUser;
        },
        bulkImageEditEnabled: {
            get() {
                return !!_.get(this.form, 'generativeMediaSettings.bulk_image_edit_enabled');
            },
            set(value) {
                // Same reason as aiLabelDisplay: a shop that never used generative media
                // has no settings row yet.
                const form = useSettingsStore().form;
                if (!form.generativeMediaSettings) {
                    this.$set(form, 'generativeMediaSettings', {});
                }
                this.$set(form.generativeMediaSettings, 'bulk_image_edit_enabled', value);
            },
        },
        // The union of what the currently licensed models actually support — no
        // second API call, the lists already came back with fetchModels(). A shop
        // with only Kling/flux-kontext (no gpt-image-2) sees no quality picker at all.
        qualityLeverOptions() {
            return _.uniq(_.flatMap(this.imageEditModels, model => model.qualityOptions || []));
        },
        // An unset field means "inherit", so the select stays empty - but it must still
        // say WHICH value that inherits to. Read off the shop's chosen default model,
        // because the levers are per-model; the *Default fields are already resolved
        // server-side (per-run arg -> CMS setting -> config), so with nothing stored
        // they carry exactly the config default this field falls back to.
        qualityPlaceholder() {
            const value = this.leverDefaultFor(
                this.imageEditModels, this.settings.default_image_edit_model_key, 'qualityOptions', 'qualityDefault',
            );
            return value ? this.$t('Default: {value}', { value: this.$t('quality.' + value) }) : null;
        },
        resolutionCapPlaceholder() {
            const value = this.leverDefaultFor(
                this.imageEditModels, this.settings.default_image_edit_model_key, 'resolutionCapOptions', 'resolutionCapDefault',
            );
            return value ? this.$t('Default: {value}', { value: value + 'px' }) : null;
        },
        videoResolutionPlaceholder() {
            const value = this.leverDefaultFor(
                this.videoModels, this.settings.default_video_model_key, 'resolutionOptions', 'resolutionDefault',
            );
            return value ? this.$t('Default: {value}', { value }) : null;
        },
        resolutionCapLeverOptions() {
            return _.uniq(_.flatMap(this.imageEditModels, model => model.resolutionCapOptions || [])).sort((a, b) => a - b);
        },
        videoResolutionLeverOptions() {
            return _.uniq(_.flatMap(this.videoModels, model => model.resolutionOptions || []));
        },
        // Which licensed model(s) each lever actually affects — a shop with only
        // gpt-image-2 needs to know "Default image quality" is about THAT model, not
        // every image-edit model it has.
        qualityLeverModels() {
            return this.imageEditModels.filter(model => (model.qualityOptions || []).length).map(model => model.label);
        },
        resolutionCapLeverModels() {
            return this.imageEditModels.filter(model => (model.resolutionCapOptions || []).length).map(model => model.label);
        },
        videoResolutionLeverModels() {
            return this.videoModels.filter(model => (model.resolutionOptions || []).length).map(model => model.label);
        },
    },
    watch: {
        // The CMS form loads asynchronously into the store, so the settings object may
        // not exist yet when created() runs. Apply defaults once it appears.
        settings: {
            immediate: true,
            handler() {
                this.applyDefaults();
            },
        },
        // Same reason the models cannot be fetched in created(): `extensions` arrives
        // with the form, so at mount this is still false and an early return there
        // would leave the pickers permanently empty.
        // Same reason for the saved choice: the pickers are pre-selected once both it and the models are there.
        contentSettings: {
            immediate: true,
            handler() {
                this.applyTextModelDefaults();
            },
        },
        textModelFeatures: {
            immediate: true,
            handler(features) {
                features.forEach(feature => this.loadTextModels(feature.key));
            },
        },
        hasGenerativeMedia: {
            immediate: true,
            handler(enabled) {
                if (enabled) {
                    this.loadModels();
                }
            },
        },
    },
    methods: {
        // The value this field falls back to when left empty.
        //
        // A lever rarely exists on every model: quality is GPT Image 2 only, video
        // resolution is Seedance only. The field is rendered because SOME licensed
        // model offers it - the "Applies to ..." hint names which - so reading the
        // default off the shop's chosen model shows nothing whenever that model is
        // not one of them. Prefer the chosen model when it declares the lever, and
        // otherwise fall back to a model that does.
        leverDefaultFor(models, chosenKey, optionsField, defaultField) {
            const declaring = _.filter(models, model => (_.get(model, optionsField) || []).length);
            const chosen = _.find(declaring, { key: chosenKey });

            return _.get(chosen || _.first(declaring), defaultField);
        },
        // A shop that never used generative media has no settings row yet, so create
        // the object rather than dropping the value on the floor.
        writeGenerativeMediaSetting(key, value) {
            const form = useSettingsStore().form;
            if (!form.generativeMediaSettings) {
                this.$set(form, 'generativeMediaSettings', {});
            }
            this.$set(form.generativeMediaSettings, key, value);
        },
        appliesToHint(models, textKey) {
            return this.$t('Applies to {models}. ', { models: models.join(', ') }) + this.$t(textKey);
        },
        // Same evaluator the APIs tab uses, so a field's `conditions` behave
        // identically wherever it is rendered (altAutoTrigger depends on
        // altAutoGenerate being on).
        checkConditions(setting) {
            let valid = true;

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            // eslint-disable-next-line no-unused-vars
            const enabledExtensions = this.enabledExtensions;

            try {
                const conditions = setting.conditions;
                if (conditions && conditions.length) {
                    conditions.forEach(condition => {
                        if (valid === false) return;
                        if (condition.type === 'eval') valid = eval(condition.condition);
                        else valid = false;
                    });
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                valid = false;
            }

            return valid;
        },
        async loadModels() {
            if (this.loading || this.videoModels.length || this.imageEditModels.length) {
                return;
            }
            this.loading = true;
            try {
                this.videoModels = await api.fetchModels('video', this.cmsId);
                this.imageEditModels = await api.fetchModels('image-edit', this.cmsId);
                this.applyDefaults();
            } finally {
                this.loading = false;
            }
        },
        // Pre-select the saved default, or the first available model — mirrors the
        // backend resolveModelKey() fallback so the picker reflects what a generation
        // would actually use.
        applyDefaults() {
            const settings = this.settings;
            if (!settings || !this.hasGenerativeMedia) {
                return;
            }
            if (!settings.default_video_model_key && this.videoModels.length) {
                this.$set(settings, 'default_video_model_key', _.get(this.videoModels, '[0].key', null));
            }
            if (!settings.default_image_edit_model_key && this.imageEditModels.length) {
                this.$set(settings, 'default_image_edit_model_key', _.get(this.imageEditModels, '[0].key', null));
            }
        },
        async loadTextModels(featureKey) {
            if (this.textModels[featureKey] || !this.cmsId) {
                return;
            }
            const models = await textGenerationApi.fetchTextModels(this.cmsId, featureKey);
            this.$set(this.textModels, featureKey, models.map(model => ({
                ...model,
                // the description is the English line of the model catalog (config/artificial-intelligence.php)
                description: this.$t(model.description),
                priceText: this.$t('{input} input, {output} output per 1M tokens', {
                    input: preciseMoneyFormat(model.input_price || 0),
                    output: preciseMoneyFormat(model.output_price || 0),
                }),
            })));
            this.applyTextModelDefaults();
        },
        // Pre-select the saved model, or else the one the feature uses until the tenant chooses, like applyDefaults()
        // does for the generative media pickers.
        applyTextModelDefaults() {
            const settings = this.contentSettings;
            if (!settings) {
                return;
            }
            this.textModelFeatures.forEach(feature => {
                const fallback = (this.textModels[feature.key] || []).find(model => model.is_default);
                if (!settings[feature.setting] && fallback) {
                    this.$set(settings, feature.setting, fallback.key);
                }
            });
        },
        // The COLLAPSED select value only — the expanded list renders
        // <model-select-option>, which owns the two-line layout.
        formatOption(model) {
            const price = preciseMoneyFormat(model.effectivePrice || 0);
            const unit = this.$tc('unit.' + model.unit, 1);
            return `${model.label} · ${this.$t('up to')} ${price}/${unit}`;
        },
    },
};
</script>

<style lang="sass" scoped>
.ai-settings
    padding: 20px
    min-height: 200px

    // Shared inline hint style. The explicit line-height is mandatory: Element's
    // el-form-item__content sets 36px (to match control height), which would
    // otherwise inflate the hint to a full row of leading.
    .help
        display: block
        margin-top: 4px
        font-size: 12px
        line-height: 1.4
        color: var(--color-text-muted, #909399)

    // Two related toggles belong side by side, not stacked: they are one decision
    // ("generate alt text" and "do it automatically"), and stacking them put a
    // full form row of dead space between them.
    &__row
        display: flex
        flex-wrap: wrap
        align-items: center
        gap: 8px 32px

    &__field
        margin-bottom: 0

    // Both pickers and the preview on one line: they are one decision (how the
    // marker looks), and the preview is only meaningful beside the controls that
    // change it.
    &__colors
        display: flex
        flex-wrap: wrap
        align-items: center
        gap: 8px 20px

    &__color
        display: flex
        align-items: center
        gap: 8px

    &__color-label
        font-size: 12px
        color: var(--color-text-muted, #909399)

    // Mirrors the storefront pill (components/app/Picture.vue): same all-caps,
    // same weights, same rounded shape - so what is previewed here is what a
    // shopper sees on the image.
    //
    // The storefront's asymmetric padding is deliberately NOT copied. It is an
    // optical correction measured against its own font (sofia-pro at 11px), and
    // under the admin's font it just parks the caps off-centre. Here the pill gets
    // a fixed height and centres its line box instead, which needs no per-font
    // number to stay right.
    &__preview
        display: inline-flex
        align-items: center
        justify-content: center
        gap: 0.375em
        height: 22px
        padding: 0 0.7em
        border-radius: 9999px
        font-size: 11px
        line-height: 1
        text-transform: uppercase

    &__preview-symbol
        font-weight: 800
        letter-spacing: 0.02em

    &__preview-variant
        font-weight: 400
        letter-spacing: 0.09em
        opacity: 0.72

    // Separates the always-available disclosure setting from the licensed
    // generative-media block below it.
    &__heading
        margin: 8px 0 16px
        padding-top: 16px
        border-top: 1px solid #ebeef5
        font-size: 14px
        font-weight: 600
        color: #303133

    // Explains what the whole "Generative media" block is for, before the reader
    // hits five bare dropdowns with no framing.
    &__intro
        margin: 0 0 20px
        font-size: 13px
        line-height: 1.5
        color: var(--color-text-muted, #909399)

    // Column heading for a capability group (Image / Video).
    &__subheading
        margin: 0 0 12px
        font-size: 12px
        font-weight: 600
        text-transform: uppercase
        letter-spacing: .04em
        color: var(--color-text-muted, #909399)
</style>
